/**
 * OptionPickerModal — Modal list picker for settings with many options.
 *
 * Shows a button with the selected value. Tap opens a slide-up modal
 * with a scrollable list (same pattern as CountryPickerModal).
 *
 * Use for 5+ options where cards would take too much vertical space.
 */

import React, { useState, useCallback } from 'react';
import { Modal, FlatList, TouchableOpacity } from 'react-native';
import { Box, HStack, VStack, Text, Pressable } from '@gluestack-ui/themed';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';
import { FS_XS, FS_SM, FS_MD, FS_LG } from '../../theme';
import type { OptionItem } from './OptionSelector';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface OptionPickerModalProps<T extends string = string> {
  /** Visible label text */
  label: string;
  /** Modal title (shown in the modal header) */
  modalTitle: string;
  /** Available options */
  options: OptionItem<T>[];
  /** Currently selected value */
  value: T;
  /** Selection callback */
  onChange: (value: T) => void;
  /** Hint text shown below the label */
  hint?: string;
  /** testID prefix */
  testID: string;
  /** Disabled state */
  disabled?: boolean;
}

// ---------------------------------------------------------------------------
// Internal: PickerRow
// ---------------------------------------------------------------------------

interface PickerRowProps {
  item: OptionItem;
  isSelected: boolean;
  fontScale: number;
  ffs: Record<string, string> | undefined;
  theme: ReturnType<typeof useDisplayTheme>['theme'];
  activeRowBg: string;
  testID: string;
  onPress: (value: string) => void;
}

const PickerRow = React.memo(function PickerRow({
  item,
  isSelected,
  fontScale,
  ffs,
  theme,
  activeRowBg,
  testID,
  onPress,
}: PickerRowProps) {
  const handlePress = useCallback(() => onPress(item.value), [onPress, item.value]);

  return (
    <TouchableOpacity
      testID={testID}
      accessibilityLabel={item.label}
      accessibilityRole="button"
      accessibilityState={{ selected: isSelected }}
      onPress={handlePress}
      activeOpacity={0.7}
    >
      <HStack
        space="md"
        p="$3"
        style={{
          alignItems: 'center',
          backgroundColor: isSelected ? activeRowBg : 'transparent',
          borderRadius: 10,
        }}
      >
        <Text
          allowFontScaling={false}
          style={{
            fontSize: FS_MD * fontScale,
            fontWeight: isSelected ? '600' : '400',
            color: isSelected ? theme.primary : theme.text,
            flex: 1,
            ...ffs,
          }}
        >
          {item.label}
        </Text>
        {isSelected && (
          <Text
            allowFontScaling={false}
            style={{ fontSize: FS_MD * fontScale, color: theme.primary }}
          >
            ✓
          </Text>
        )}
      </HStack>
    </TouchableOpacity>
  );
});

// ---------------------------------------------------------------------------
// OptionPickerModal
// ---------------------------------------------------------------------------

function OptionPickerModalInner<T extends string = string>({
  label,
  modalTitle,
  options,
  value,
  onChange,
  hint,
  testID,
  disabled,
}: OptionPickerModalProps<T>) {
  const { theme, fontScale, ffs } = useDisplayTheme();
  const insets = useSafeAreaInsets();
  const [modalVisible, setModalVisible] = useState(false);
  const activeRowBg = theme.isDark ? '#1E3A5F' : '#EBF5FF';

  const selectedOption = options.find((o) => o.value === value);

  const handleSelect = useCallback(
    (val: string) => {
      onChange(val as T);
      setModalVisible(false);
    },
    [onChange],
  );

  const renderItem = useCallback(
    ({ item }: { item: OptionItem<T> }) => (
      <PickerRow
        item={item}
        isSelected={value === item.value}
        fontScale={fontScale}
        ffs={ffs}
        theme={theme}
        activeRowBg={activeRowBg}
        testID={`${testID}-row-${item.value}`}
        onPress={handleSelect}
      />
    ),
    [value, fontScale, ffs, theme, activeRowBg, testID, handleSelect],
  );

  const keyExtractor = useCallback((item: OptionItem<T>) => item.value, []);

  return (
    <>
      <VStack space="sm">
        {/* Label */}
        <Text
          allowFontScaling={false}
          style={{
            fontSize: FS_SM * fontScale,
            fontWeight: '600',
            color: theme.textSecondary,
            ...ffs,
          }}
        >
          {label}
        </Text>

        {/* Hint */}
        {hint != null && (
          <Text
            allowFontScaling={false}
            style={{
              fontSize: FS_XS * fontScale,
              color: theme.textSecondary,
              ...ffs,
            }}
          >
            {hint}
          </Text>
        )}

        {/* Trigger button */}
        <TouchableOpacity
          testID={`${testID}-trigger`}
          accessibilityLabel={`${label}: ${selectedOption?.label ?? ''}`}
          accessibilityRole="button"
          onPress={() => setModalVisible(true)}
          activeOpacity={0.7}
          disabled={disabled}
        >
          <HStack
            p="$3"
            rounded="$lg"
            space="md"
            style={{
              backgroundColor: selectedOption ? activeRowBg : theme.inputBg,
              borderWidth: 1,
              borderColor: selectedOption ? theme.primary : theme.border,
              alignItems: 'center',
              opacity: disabled ? 0.5 : 1,
            }}
          >
            <Text
              allowFontScaling={false}
              style={{
                fontSize: FS_MD * fontScale,
                fontWeight: '600',
                color: selectedOption ? theme.primary : theme.textSecondary,
                flex: 1,
                ...ffs,
              }}
            >
              {selectedOption?.label ?? ''}
            </Text>
            <Text
              allowFontScaling={false}
              style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary }}
            >
              ▼
            </Text>
          </HStack>
        </TouchableOpacity>
      </VStack>

      {/* Modal */}
      <Modal
        visible={modalVisible}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setModalVisible(false)}
      >
        <Box
          flex={1}
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          justifyContent="flex-end"
        >
          {/* Backdrop */}
          <Pressable
            testID={`${testID}-backdrop`}
            onPress={() => setModalVisible(false)}
            style={{ flex: 1 }}
          />

          {/* Card */}
          <Box
            style={{
              backgroundColor: theme.card,
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              maxHeight: '60%',
            }}
          >
            {/* Header */}
            <HStack
              p="$4"
              style={{
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottomWidth: 1,
                borderBottomColor: theme.border,
              }}
            >
              <Text
                allowFontScaling={false}
                accessibilityRole="header"
                style={{
                  fontSize: FS_LG * fontScale,
                  fontWeight: '700',
                  color: theme.text,
                  flex: 1,
                  ...ffs,
                }}
              >
                {modalTitle}
              </Text>
              <TouchableOpacity
                testID={`${testID}-close`}
                accessibilityLabel="Close"
                accessibilityRole="button"
                onPress={() => setModalVisible(false)}
                hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
              >
                <Text
                  allowFontScaling={false}
                  style={{
                    fontSize: FS_LG * fontScale,
                    color: theme.textSecondary,
                    fontWeight: '300',
                  }}
                >
                  ✕
                </Text>
              </TouchableOpacity>
            </HStack>

            {/* Options list */}
            <FlatList
              data={options}
              renderItem={renderItem}
              keyExtractor={keyExtractor}
              keyboardShouldPersistTaps="handled"
              contentContainerStyle={{ paddingHorizontal: 8, paddingTop: 8, paddingBottom: Math.max(24, insets.bottom) }}
              showsVerticalScrollIndicator={false}
            />
          </Box>
        </Box>
      </Modal>
    </>
  );
}

export const OptionPickerModal = React.memo(OptionPickerModalInner) as typeof OptionPickerModalInner;
